import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import api from '../services/api';
import { getUser } from '../services/auth';
import toast from '../services/toast';
import { parseDataJogo } from '../utils/dataJogo';
import Icon from '../components/Icon';
import Crest from '../components/Crest';
import './Palpites.css';

function formatData(d) {
  return parseDataJogo(d).toLocaleString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function Palpites() {
  const location = useLocation();
  const navigate = useNavigate();
  const jogoInicial = location.state?.jogo || null;
  const palpiteExistente = location.state?.palpiteExistente || null;
  const user = getUser();

  const [meusPalpites, setMeusPalpites] = useState([]);
  const [jogo, setJogo] = useState(jogoInicial);
  const [form, setForm] = useState({
    palpite_casa: palpiteExistente != null ? String(palpiteExistente.palpite_casa) : '',
    palpite_fora: palpiteExistente != null ? String(palpiteExistente.palpite_fora) : '',
  });
  const [estatisticas, setEstatisticas] = useState([]);
  const [totalPalpites, setTotalPalpites] = useState(0);
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.bloqueado) return;
    api.get('/palpites/meus').then(setMeusPalpites).catch(() => {});
  }, []);

  useEffect(() => {
    if (meusPalpites.length === 0) return;
    const jaFestejados = JSON.parse(localStorage.getItem('palpitar_celebrado') || '[]');
    const novos = meusPalpites.filter(p => !!p.finalizado && p.pontos_ganhos > 0 && !jaFestejados.includes(p.id));
    if (novos.length === 0) return;
    confetti({ particleCount: 120, spread: 80, origin: { y: 0.55 }, colors: ['#6c63ff', '#22c55e', '#f59e0b', '#fff'] });
    novos.forEach(p => toast.success(`🎉 Você acertou: ${p.time_casa_nome} ${p.palpite_casa}x${p.palpite_fora} ${p.time_fora_nome}!`));
    localStorage.setItem('palpitar_celebrado', JSON.stringify([...jaFestejados, ...novos.map(p => p.id)]));
  }, [meusPalpites]);

  useEffect(() => {
    if (user?.bloqueado || !jogo) { setEstatisticas([]); setTotalPalpites(0); return; }
    api.get(`/palpites/estatisticas/${jogo.id}`)
      .then(data => {
        setEstatisticas(data.placares);
        setTotalPalpites(data.total);
      })
      .catch(() => {});
  }, [jogo]);

  if (user?.bloqueado) {
    return (
      <div className="bloqueado-msg">
        <div className="bloqueado-icon">🔒</div>
        <h2>Acesso suspenso</h2>
        <p>Sua participação está temporariamente suspensa devido à pendência de pagamento. Envie um comprovante PIX para voltar a participar dos palpites.</p>
        <button className="btn btn-primary" onClick={() => navigate('/comprovante')}>Enviar Comprovante PIX</button>
      </div>
    );
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErro('');
    if (form.palpite_casa === '' || form.palpite_fora === '') {
      return setErro('Informe os gols dos dois times.');
    }
    setLoading(true);
    try {
      await api.post('/palpites', {
        jogo_id: jogo.id,
        palpite_casa: Number(form.palpite_casa),
        palpite_fora: Number(form.palpite_fora),
      });
      toast.success(palpiteExistente ? 'Palpite atualizado com sucesso!' : 'Palpite registrado com sucesso!');
      const [novos, stats] = await Promise.all([
        api.get('/palpites/meus'),
        api.get(`/palpites/estatisticas/${jogo.id}`),
      ]);
      setMeusPalpites(novos);
      setEstatisticas(stats.placares);
      setTotalPalpites(stats.total);
      setJogo(null);
    } catch (err) {
      setErro(err.message);
    } finally {
      setLoading(false);
    }
  }

  // Estatísticas do placar atual sendo digitado
  const statAtual = form.palpite_casa !== '' && form.palpite_fora !== ''
    ? estatisticas.find(e => e.palpite_casa === Number(form.palpite_casa) && e.palpite_fora === Number(form.palpite_fora))
    : null;

  return (
    <div>
      <h1 className="page-title">Palpites</h1>

      {jogo && (
        <div className="palpite-form-card">
          <div className="palpite-jogo-info">
            <span className="palpite-campeonato">{jogo.campeonato_nome}</span>
            {jogo.rodada_nome && <span className="palpite-rodada">{jogo.rodada_nome}</span>}
          </div>
          {palpiteExistente && (
            <div className="palpite-editando-aviso"><Icon name="edit" size={13} /> Editando palpite existente</div>
          )}
          <p className="palpite-data">{formatData(jogo.data_jogo)}</p>

          <div className="palpite-confronto">
            <div className="palpite-time">
              <Crest nome={jogo.time_casa_nome} escudo={jogo.time_casa_escudo} size="lg" />
              <span>{jogo.time_casa_nome}</span>
              <input
                type="number" min="0" max="99"
                value={form.palpite_casa}
                onChange={e => setForm({ ...form, palpite_casa: e.target.value })}
                placeholder="0"
                className="gols-input"
              />
            </div>
            <span className="palpite-vs">×</span>
            <div className="palpite-time right">
              <Crest nome={jogo.time_fora_nome} escudo={jogo.time_fora_escudo} size="lg" />
              <span>{jogo.time_fora_nome}</span>
              <input
                type="number" min="0" max="99"
                value={form.palpite_fora}
                onChange={e => setForm({ ...form, palpite_fora: e.target.value })}
                placeholder="0"
                className="gols-input"
              />
            </div>
          </div>

          {statAtual && (
            <div className="placar-popularidade-hint">
              <Icon name="users" size={13} /> {statAtual.quantidade} {Number(statAtual.quantidade) === 1 ? 'pessoa escolheu' : 'pessoas escolheram'} este placar
            </div>
          )}

          {totalPalpites > 0 && (
            <div className="estatisticas-box">
              <div className="estatisticas-header">
                <span className="estatisticas-titulo"><Icon name="users" size={12} /> Popularidade dos palpites</span>
                <span className="estatisticas-total">{totalPalpites} palpite{totalPalpites !== 1 ? 's' : ''}</span>
              </div>
              <div className="estatisticas-lista">
                {estatisticas.map((e, i) => {
                  const isSelecionado = form.palpite_casa !== '' && form.palpite_fora !== '' &&
                    Number(form.palpite_casa) === e.palpite_casa && Number(form.palpite_fora) === e.palpite_fora;
                  const pct = Math.round((Number(e.quantidade) / totalPalpites) * 100);
                  return (
                    <div key={i} className={`estat-row${isSelecionado ? ' selecionado' : ''}`}>
                      <span className="estat-placar">{e.palpite_casa}x{e.palpite_fora}</span>
                      <div className="estat-barra-wrap">
                        <div className="estat-barra" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="estat-count"><Icon name="users" size={11} /> {e.quantidade}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {erro && <div className="alert alert-error">{erro}</div>}

          <div className="palpite-actions">
            <button className="btn btn-secondary" onClick={() => { setJogo(null); setErro(''); }}>
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
              {loading ? 'Enviando...' : palpiteExistente ? 'Salvar Edição' : 'Confirmar Palpite'}
            </button>
          </div>
        </div>
      )}

      <h2 className="sub-title">Meus Palpites</h2>
      {meusPalpites.length === 0 && <p className="empty-msg">Você ainda não fez palpites.</p>}
      {meusPalpites.map(p => {
        const finalizado = !!p.finalizado;
        return (
          <div key={p.id} className={`palpite-item${finalizado ? (p.pontos_ganhos > 0 ? ' acerto' : ' erro') : ''}`}>
            <div className="palpite-item-header">
              <span className="palpite-camp">{p.campeonato_nome}</span>
              {finalizado && p.pontos_ganhos > 0 && <span className="badge badge-success">+{p.pontos_ganhos} pts</span>}
              {finalizado && p.pontos_ganhos === 0 && <span className="badge badge-danger">0 pts</span>}
              {!finalizado && <span className="badge badge-info">Aguardando</span>}
            </div>
            <div className="palpite-placar-row">
              <span className="palpite-time-nome">{p.time_casa_nome}</span>
              <div className="palpite-placar-box">
                <span>{p.palpite_casa} x {p.palpite_fora}</span>
                {finalizado && <span className="resultado-real">({p.resultado_casa} x {p.resultado_fora})</span>}
              </div>
              <span className="palpite-time-nome right">{p.time_fora_nome}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
